/**
 * Spend classes — the vocabulary the `/tina/home` cost chart filters on.
 *
 * The chart's pills used to be raw `content_type` values and `/insights/cost`
 * was called without `job_class`, so the server's `"scrape"` default hid every
 * enrichment row: enrichment spend was invisible in IMS. IMS now asks for
 * `job_class=all` and classifies each row here, because the pills are a *filter
 * over one aggregation*, not three requests.
 *
 * ## Why the classifier tests `stage` as well as `content_type`
 *
 * There are two shapes of enrichment spend and only one of them is visible in
 * `content_type`:
 *
 * - **external jobs** — their own `events` row, `content_type='enrichment_*'`,
 *   running the `enrich` / `embed` / `translate` stages;
 * - **the in-pipeline tail** — the `enrichment` / `embedding` / `translation`
 *   stages appended to a `music_event` / `cinema_screening` walk, so their cost
 *   rows carry `content_type='music_event'`.
 *
 * A `content_type`-only test therefore bills the tail's LLM spend to *scraping*.
 * Six stage names, not two.
 *
 * **SQL twin:** tina `src/db/queries/events.py`'s `ENRICHMENT_COST_CLASS_EXPR`
 * (`content_type LIKE 'enrichment\_%' OR stage IN ('enrich','embed',
 * 'enrichment','embedding')`) and `SCRAPE_COST_CLASS_EXPR`, which is what
 * `/insights/cost?job_class=` filters on server-side. Keep the two in step: this
 * module must classify `job_class=all` rows into exactly the buckets the server
 * would have returned for `job_class=scrape` and `job_class=enrichment`.
 */
import { isRefreshContentType } from "@/lib/tina/content-types";
import type { CostDailyRow, EventsDailyRow } from "@/types/tina";

/**
 * Every stage name that is enrichment work, in either shape.
 *
 * `enrich` / `embed` / `translate` are the standalone job stages;
 * `enrichment` / `embedding` / `translation` are the in-walk tail. Missing the
 * tail names silently under-reports enrichment and over-reports scraping — see
 * the module docstring.
 *
 * Twin of tina `src/db/queries/events.py::_ENRICHMENT_STAGE_NAMES`.
 */
export const ENRICHMENT_STAGES = [
  "enrich",
  "embed",
  "translate",
  "enrichment",
  "embedding",
  "translation",
] as const;

export type EnrichmentStage = (typeof ENRICHMENT_STAGES)[number];

const ENRICHMENT_STAGE_SET: ReadonlySet<string> = new Set(ENRICHMENT_STAGES);

/** True for the six stage names that are enrichment/embedding/translation work. */
export function isEnrichmentStage(
  stage: string | null | undefined,
): stage is EnrichmentStage {
  return ENRICHMENT_STAGE_SET.has(stage ?? "");
}

export const SPEND_CLASSES = [
  "music",
  "cinema",
  "theater",
  "enrichment",
  // Event refresh (moo-4794) — its own class, not folded into music. Tina bills
  // it that way too: `REFRESH_COST_CLASS_EXPR` is `content_type =
  // 'music_event_refresh'` and `SCRAPE_COST_CLASS_EXPR` subtracts it, so a
  // refresh night charged to "Music" would be a number neither side agrees on.
  "refresh",
  "other",
] as const;

export type SpendClass = (typeof SPEND_CLASSES)[number];

export const SPEND_CLASS_LABEL: Record<SpendClass, string> = {
  music: "Music",
  cinema: "Cinema",
  theater: "Theater",
  enrichment: "Enrichment",
  refresh: "Refresh",
  other: "Other",
};

/**
 * Chart/legend order — enrichment and refresh after the scrape verticals, so those stay adjacent.
 */
export const SPEND_CLASS_ORDER: readonly SpendClass[] = SPEND_CLASSES;

/**
 * True for any `content_type` starting with `enrichment_` — the SQL twin's own
 * test (`content_type LIKE 'enrichment\_%'`), not the closed list of eleven
 * entity flows `isEnrichmentContentType` (`@/lib/tina/content-types`) checks.
 *
 * The artist-field-locks curator-review job (`enrichment_artist_curation`,
 * 2026-09-28) matches this prefix but is not one of the eleven entity flows —
 * it enriches no new entity kind, it holds a proposed write against an
 * artist's own `enrichment_artist` job — so the exact-list check silently
 * dropped its cost into "Other" instead of "Enrichment". Keep this a plain
 * prefix test, exactly like the SQL, rather than growing the closed list
 * every time a new non-entity enrichment job type is added.
 */
function isEnrichmentCostContentType(
  contentType: string | null | undefined,
): boolean {
  return (contentType ?? "").startsWith("enrichment_");
}

/** Which spend class a scrape `content_type` belongs to. */
function scrapeClassOf(contentType: string | null | undefined): SpendClass {
  switch (contentType) {
    case "music_event":
      return "music";
    case "cinema_screening":
      return "cinema";
    case "theater_performance":
      return "theater";
    default:
      return "other";
  }
}

/**
 * Spend class of one `/insights/cost` row.
 *
 * Order matters: the enrichment tests come first, so an `enrichment` stage
 * inside a `music_event` walk lands in `enrichment` and not `music`.
 */
export function spendClassForCost(
  row: Pick<CostDailyRow, "stage" | "content_type">,
): SpendClass {
  if (isEnrichmentCostContentType(row.content_type)) return "enrichment"; // external jobs
  if (isEnrichmentStage(row.stage)) return "enrichment"; // in-pipeline tail
  // After both enrichment tests, mirroring tina's `SCRAPE_COST_CLASS_EXPR`
  // (`NOT enrichment_cost AND NOT refresh`). Equivalent in practice — a refresh
  // walk runs fetch/refresh_* and never an enrich/embed stage — but the order
  // keeps this a twin of the SQL rather than a coincidence that agrees with it.
  if (isRefreshContentType(row.content_type)) return "refresh";
  return scrapeClassOf(row.content_type);
}

/**
 * Spend class of one `/insights/events` row.
 *
 * `EventsDailyRow` has **no** `stage` column, and it does not need one: the
 * in-pipeline tail produces no `events` row of its own, so an `enrichment_*`
 * content type is both the only signal available and the correct one. Tina says
 * the same thing in `metric_events`' docstring.
 *
 * An enrichment row here counts *jobs*, which is a different unit from a scraped
 * event — never sum the two.
 */
export function spendClassForEvents(
  row: Pick<EventsDailyRow, "content_type">,
): SpendClass {
  if (isEnrichmentCostContentType(row.content_type)) return "enrichment";
  if (isRefreshContentType(row.content_type)) return "refresh";
  return scrapeClassOf(row.content_type);
}
